import { ZfDialog, ZfDialogContent, ZfDialogFooter, ZfStack, ZfTextButton, ZfTypography } from '@zafire/react';
import type { FunctionComponent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useForm, type UseFormSetValue, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCheckDocument, useCreateDocument, useDeleteDocument, useEnabledDomiciles, useUpdateDocument } from '../../../api';
import type { DocumentDto, DomicileDto, SupportedLanguage } from '../../../api/generated/client';
import { AVAILABLE_LANGUAGES } from '../../../i18n/i18nextInit';
import { DomicileSelection } from '../domicile-selection';
import { AdminDocumentUpload } from './AdminDocumentUpload';

interface IProps {
    document: DocumentDto;
}

export const DocumentEditor: FunctionComponent<IProps> = ({ document: initialDocument }) => {
    const { t } = useTranslation();
    const { mutate: createDocument } = useCreateDocument();
    const { mutate: updateDocument } = useUpdateDocument();
    const { mutate: deleteDocument } = useDeleteDocument();
    const { data: documentClientPackage } = useCheckDocument(initialDocument.id);
    const { data: availableDomiciles } = useEnabledDomiciles();

    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    const { control, handleSubmit, reset, setValue } = useForm<DocumentDto>({
        mode: 'onTouched',
        reValidateMode: 'onChange',
        defaultValues: initialDocument,
    });
    const setFormValue: UseFormSetValue<DocumentDto> = (name, value, options) => setValue(name, value, { shouldDirty: true, ...options });
    const { isDirty, isValid } = useFormState<DocumentDto>({ control });
    const [documentId, global, includedDomiciles, excludedDomiciles, name, documentFiles] = useWatch({
        control,
        name: ['id', 'global', 'includedDomiciles', 'excludedDomiciles', 'name', 'documentFiles'],
    });

    const isCreateMode = documentId === -1;

    // Validate that all available languages are present exactly once in both name and documentFiles
    const areAllLanguagesPresent = useMemo(() => {
        if (!name || !documentFiles) return false;

        // Check name object: each language should appear exactly once
        const nameLanguages = Object.keys(name).filter((key) => AVAILABLE_LANGUAGES.includes(key as SupportedLanguage));
        const nameLanguageSet = new Set(nameLanguages);

        // Check documentFiles array: each language should appear exactly once
        const fileLanguages = documentFiles.map((file) => file.language).filter((lang) => lang !== undefined && AVAILABLE_LANGUAGES.includes(lang));
        const fileLanguageSet = new Set(fileLanguages);

        // All languages must be present in both
        const allLanguagesPresent = AVAILABLE_LANGUAGES.every((lang) => nameLanguageSet.has(lang) && fileLanguageSet.has(lang));

        // No duplicates: sets should have exactly the same size as available languages
        const noDuplicatesInName = nameLanguageSet.size === AVAILABLE_LANGUAGES.length;
        const noDuplicatesInFiles = fileLanguageSet.size === AVAILABLE_LANGUAGES.length;

        return allLanguagesPresent && noDuplicatesInName && noDuplicatesInFiles;
    }, [name, documentFiles]);

    const isSaveDisabled = !isDirty || !isValid || !areAllLanguagesPresent;

    useEffect(() => {
        reset(initialDocument);
        setShowDeleteDialog(false);
    }, [initialDocument, reset]);

    const onDomicileChange = (includedDomiciles: DomicileDto[], excludedDomiciles: DomicileDto[], global: boolean) => {
        setFormValue('global', global);
        setFormValue('excludedDomiciles', excludedDomiciles);
        setFormValue('includedDomiciles', includedDomiciles);
    };

    const onSaveChangesClicked = handleSubmit((document) => (isCreateMode ? createDocument : updateDocument)(document));

    const onConfirmPopup = () => {
        deleteDocument(documentId);
        if (documentClientPackage) setShowDeleteDialog(true);
        setShowDeleteDialog(true);
    };

    const onDeleteDoc = () => {
        if (documentClientPackage?.documentStatus === false) {
            setShowDeleteDialog(true);
        } else if (documentClientPackage?.documentStatus === true) {
            deleteDocument(documentId);
            setShowDeleteDialog(false);
        } else {
            setShowDeleteDialog(false);
        }
    };

    return (
        <ZfStack direction="column" spacing="large" style={{ position: 'relative' }}>
            <ZfStack direction="row" alignment="center" justify="space-between">
                <ZfTypography variant="heading-medium" style={{ flex: 1 }}>
                    {isCreateMode ? t('contentAdmin.documentEditorTitleCreate') : `${t('contentAdmin.documentEditorTitleEdit')} ${documentId}`}
                </ZfTypography>
                <ZfStack direction="row" spacing="small">
                    <ZfTextButton hierarchy="secondary" disabled={isSaveDisabled} onClick={onSaveChangesClicked}>
                        {t('contentAdmin.documentEditorSave')}
                    </ZfTextButton>
                    {!isCreateMode ? (
                        <ZfTextButton hierarchy="secondary" onClick={onDeleteDoc}>
                            {t('contentAdmin.documentEditorDelete')}
                        </ZfTextButton>
                    ) : null}
                </ZfStack>
            </ZfStack>
            <AdminDocumentUpload isCreateMode={isCreateMode} control={control} setValue={setFormValue} />
            <DomicileSelection
                global={global}
                onChange={onDomicileChange}
                available={availableDomiciles ?? []}
                excludedIn={excludedDomiciles}
                includedIn={includedDomiciles}
            />
            <ZfDialog show={showDeleteDialog} size="s">
                <ZfDialogContent>
                    <div>{t('clientPackageDependency')}</div>
                </ZfDialogContent>
                <ZfDialogFooter>
                    <ZfStack slot="primary" direction="row" spacing="small">
                        <ZfTextButton hierarchy="tertiary" size="medium" onClick={() => setShowDeleteDialog(false)}>
                            {t('modalCloseButton')}
                        </ZfTextButton>
                        <ZfTextButton size="medium" onClick={onConfirmPopup}>
                            {t('modalConfirmButton')}
                        </ZfTextButton>
                    </ZfStack>
                </ZfDialogFooter>
            </ZfDialog>
        </ZfStack>
    );
};
